import ExpireAlert from "components/common/modal/ExpireAlert";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { BrowserRouter, useLocation } from "react-router-dom";
import { postFlagCheck, getEnvConfig } from "services/apis";
import { signInUsingToken } from "services/authService";
import { getSessionStorageItem } from "services/storageService";
import { APP_VERSION, SERVICE_TYPE } from "utils/constants";
import AppRoutes from "./routes";
// import useRouteChangeTracker from "hooks/useRouteChangeTracker";
import useInterval from "hooks/useInterval";
import ReactGA from "react-ga4";
import MetaTag from "utils/MetaTag";
import { useGoodsListQuery } from "hooks/queries/goods";
import { useSelector } from "react-redux";

const FlagChecker = () => {
  const location = useLocation();
  const isGateway = location.pathname.includes("/auth/gateway");

  // Apple Safari 로그인 흐름: 메인 페이지가 Apple OAuth로 리다이렉트되었다가
  // Gateway로 돌아오면 full page reload가 발생함. Gateway → 홈 이동 직후
  // 첫 flag 쿼리가 발동되는데, Safari가 cross-origin 리다이렉트 체인 직후
  // flag.kordagw.co.kr으로의 CORS preflight를 일시적으로 차단/실패시키는 경우가 있음.
  // 한 번 실패만으로 KILL 처리하면 정상 사용자가 work.html로 잘못 이동하므로
  // queryFn에서 catch하지 않고 React Query의 retry로 재시도 후 판단하도록 변경.
  const { data: flag = "NONE", isError } = useQuery(
    "flag",
    () => {
      return postFlagCheck({
        versionName: SERVICE_TYPE === "DEVELOPMENT" ? "999.0.0" : APP_VERSION,
        os: "web",
        appName: "kumbang-mall",
      }).then(({ data: { ok, response } }) => {
        if (ok) {
          return response.action;
        }
        // 서버가 ok=false 응답: 명시적 서비스 중단 신호 → KILL
        return "KILL";
      });
      // 네트워크 오류는 throw되어 React Query가 retry함
    },
    {
      enabled: !isGateway,
      retry: 2,
      retryDelay: (attemptIndex) => 1000 * (attemptIndex + 1), // 1s, 2s
    },
  );

  useEffect(() => {
    // 서버가 KILL 응답, 또는 모든 retry가 실패한 경우 (서비스 중단)
    if (flag === "KILL" || isError) {
      window.location.href = "https://guide.kumbang.co.kr/work.html";
    }
  }, [flag, isError]);

  return null;
};

const VersionChecker = () => {
  const getVersion = (version) => {
    const reg = /(\d*)\.(\d*)\.(\d*)/;
    const match = version.match(reg);

    const [FULL, MAJOR, MINOR, PATCH] = match;
    return { FULL, MAJOR, MINOR, PATCH };
  };

  const [checkCycle, setCheckCycle] = useState(3); // second
  useInterval(async () => {
    // API 호출 주기
    const cycle = await getEnvConfig("MALL_FRONT_REFRESH_PERIOD").then(
      ({ data: { success, data } }) => {
        if (success) {
          return data?.value;
        }
      },
    );
    // Front 최종 버전
    const latest = await getEnvConfig("MALL_FRONT_LATEST_VERSION").then(
      ({ data: { success, data } }) => {
        if (success) {
          return data?.value;
        }
      },
    );
    cycle && setCheckCycle(cycle);

    if (latest && APP_VERSION) {
      const appVersion = getVersion(APP_VERSION);
      const latestVersion = getVersion(latest);

      if (+latestVersion.MAJOR > +appVersion.MAJOR) {
        location.reload();
      } else if (+latestVersion.MAJOR === +appVersion.MAJOR) {
        if (+latestVersion.MINOR > +appVersion.MINOR) {
          location.reload();
        } else if (+latestVersion.MINOR === +appVersion.MINOR) {
          if (+latestVersion.PATCH > +appVersion.PATCH) {
            location.reload();
          }
        }
      }
    }
  }, checkCycle * 1000);
  return null;
};

function App() {
  const gaId = process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID;
  let accessToken = getSessionStorageItem("accessToken");
  const { isLoading } = useGoodsListQuery();

  const auth = useSelector((state) => state.auth);
  const { isSigned } = auth;

  useLayoutEffect(() => {
    // 윈도우가 프로젝트를 벗어났다가 돌아왔을 경우 자동로그인
    if (accessToken) {
      signInUsingToken(accessToken);
    }
  }, []);


  // GA 설정
  useEffect(() => {
    if (!window.location.href.includes("localhost") && gaId) {
      ReactGA.initialize(gaId);
    }
  }, []);

  // // 재랜더링시에 실행되게 해준다.
  useEffect(() => {
    // init 해주기 전에 clean up 을 해준다.
    window.Kakao.cleanup();
    // 자신의 js 키를 넣어준다.
    window.Kakao.init(process.env.REACT_APP_JS_KEY);
    // 잘 적용되면 true 를 뱉는다.
  }, []);

  useLayoutEffect(() => {
    if (window.vreviewWidget) {
      // 브이리뷰 위젯 생성 함수
      window.vreviewWidget.initialize({
        uuid: process.env.REACT_APP_VREVIEW_UUID,
        originMemberId: "",
      });
      // 로그인되면 originMemberId(userId) 세팅
      // 마이페이지 위젯을 위한 함수
      if (isSigned) {
        window.vreviewWidget.login(auth?.userId);
      }
    }
  }, [window.vreviewWidget, isSigned]);

  useMemo(() => {
    // window.location.reload();
  }, []);

  if (isLoading) return null;

  return (
    <>
      <MetaTag
        keywords={
          "골드바, 순금, 24k,금값시세,금시세,순금시세,오늘금시세,골드바 가격,귀금속,금"
        }
      />
      <BrowserRouter>
        <VersionChecker />
        <FlagChecker />
        {/* <GoogleAnalyticsTracker /> */}
        <ExpireAlert />
        <AppRoutes />
      </BrowserRouter>
    </>
  );
}

export default App;

// Google Analytic
// const GoogleAnalyticsTracker = () => {
//   useRouteChangeTracker();
//   return null;
// };
